import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

// Vimeo Player SDK
import Player from "@vimeo/player";

// Toolkit
import { ThemeContext } from "sj-ui-toolkit/src/theme/ThemeContext";

// SASS module
import {
  fetchUserVideoSettings,
  updateUserAttributes,
  updateWatchedVideo,
} from "sj-ui-toolkit/src/api";

import S from "./_styles.module.scss";

const VideoPlayer = ({ videoId, lessonId, watchedVideo, assessmentId }) => {
  const { appliedTheme } = useContext(ThemeContext);
  const playerRef = useRef(null);
  const watchedRef = useRef(watchedVideo);
  const lastPercentRef = useRef(0);
  const progressPercentRef = useRef(0);
  const videoSettingsRef = useRef(null);
  const volumeTimeoutRef = useRef(null);

  // hard-code colors for now
  const colors = useMemo(() => {
    return appliedTheme === "atlas-light"
      ? ["eeeef0", "3b82f6", "1e2126", "eeeef0"]
      : ["282d33", "3b82f6", "ffffff", "282d33"];
  }, [appliedTheme]);

  // Small, continuous differences indicate normal playback so we count towards watch progress
  // Big jumps don't count towards progress, but we still update lastPercent so they can start accruing progress again
  // Rewinds are ignored so that can't simply watch the same segments over and over to reach 80%
  const handleTimeUpdate = useCallback(
    data => {
      if (watchedRef.current) return;

      const currentPercent = data.percent;
      const difference = currentPercent - lastPercentRef.current;

      if (difference > 0 && difference < 0.06) {
        lastPercentRef.current = currentPercent;
        progressPercentRef.current += difference;

        if (progressPercentRef.current >= 0.8) {
          watchedRef.current = true;
          updateWatchedVideo({ lessonId, assessmentId });
        }
      } else if (difference >= 0.06) {
        lastPercentRef.current = currentPercent;
      }
    },
    [lessonId, assessmentId]
  );

  useEffect(() => {
    if (!videoId) return;

    const player = new Player(playerRef.current, {
      id: videoId,
      colors: colors,
    });

    fetchUserVideoSettings().then(settings => {
      if (settings) {
        player.setVolume(settings.volume || 1.0);
        player.setPlaybackRate(settings.playbackRate || 1.0);
        if (settings.textTrack) {
          player.enableTextTrack(settings.textTrack);
        }
      }
      videoSettingsRef.current = settings;
    });

    player.on("volumechange", data => {
      clearTimeout(volumeTimeoutRef.current);
      volumeTimeoutRef.current = setTimeout(() => {
        updateUserAttributes("videoSettings", {
          ...videoSettingsRef.current,
          volume: data.volume,
        });
      }, 250);
    });

    player.on("playbackratechange", data => {
      updateUserAttributes("videoSettings", {
        ...videoSettingsRef.current,
        playbackRate: data.playbackRate,
      });
    });

    player.on("texttrackchange", data => {
      updateUserAttributes("videoSettings", {
        ...videoSettingsRef.current,
        textTrack: data.language,
      });
    });

    if (!watchedRef.current) {
      player.on("timeupdate", data => handleTimeUpdate(data));
    }

    return () => {
      clearTimeout(volumeTimeoutRef.current);
      player.destroy();
    };
  }, [videoId, handleTimeUpdate]);

  return <div className={S.frameContainer} ref={playerRef}></div>;
};

export default VideoPlayer;
